import axios from '@/lib/axios.ts'
import { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { useToast } from 'vue-toastification'

import useCustomMutation from '@/composables/useCustomMutation.ts'

import { MutationParams } from '@/interfaces/mutationParams.ts'

import { Role } from '@/modules/roles/composables/useRoles.ts'

interface Props {
	immediate?: boolean
	params?: Params
}

interface Params {
	id: number | string
}

interface UpdateParams {
	id: number | string
	roles: number[]
	// Al bajar a Traveler a un usuario con subordinados el backend exige una de dos:
	// recolgarlos de un Supervisor (newSupervisorId) o dejarlos libres sin
	// supervisor (releaseSubordinates).
	newSupervisorId?: number
	releaseSubordinates?: boolean
}

const getUserRole = async (params: Params): Promise<Role[]> => {
	const { data } = await axios.get(`/api/users/${params.id}/roles`)

	return data
}

const updateRole = async (
	params: UpdateParams
): Promise<Role[] | undefined> => {
	const { data } = await axios.post(
		`/api/users/${params.id}/roles`,
		{
			roles: params.roles,
			// snake_case explícito en el wire. (El middleware ParseToSnakeBefore de
			// BookingV2 también adapta camelCase, pero así no depende de él.)
			new_supervisor_id: params.newSupervisorId,
			release_subordinates: params.releaseSubordinates,
		},
		{ errorHandling: false } as CustomAxiosRequestConfig,
	)

	return data
}

const useUserRole = ({ immediate = true, params }: Props) => {
	const toast = useToast()
	const { data, isLoading } = useQuery(
		['userRole', params],
		() =>
			getUserRole({
				id: params?.id!,
			}),
		{
			enabled: immediate,
		}
	)

	const updateRoleMutation = useMutation(
		({ params }: MutationParams<UpdateParams>) => updateRole(params),
		useCustomMutation(toast, 'delete')
	)

	return {
		data,
		isLoading,

		updateRole: updateRoleMutation.mutate,
		updateRoleLoading: updateRoleMutation.isLoading,
		updateRoleAsync: updateRoleMutation.mutateAsync,
	}
}

export default useUserRole
